/**
 * The backoff ladder that watches background work — 2s, then 5s, then 15s, then stop.
 *
 * It is PORTED, not invented. The htmx panel it replaces learned these numbers the
 * expensive way (`services/authoring-web/src/routes/jobs.ts`): its first cut polled with
 * `hx-trigger="load, every 2s"` and `hx-swap="outerHTML"`, and because the swap creates a
 * brand-new element the `load` trigger re-fired immediately — so each card polled as fast
 * as round-trip latency allowed, *plus* the timer, for the whole run. Four sources being
 * read at once consumed the sidecar's entire per-account budget and 429'd the owner's
 * next real click. The fix was this ladder, and it moves across intact:
 *
 *   • fast while somebody is watching (the first ~10 seconds are the ones an owner
 *     actually stares at, right after pressing Add),
 *   • cheap once a long crawl settles in (steady state is 4 requests a minute),
 *   • and a HARD STOP, so a tab left open on a desk overnight is not still polling in the
 *     morning.
 *
 * ONE POLL FOR THE WHOLE PAGE, not one per card. `GET /jobs` returns every job in one
 * response, so a screen with six sources in flight costs exactly what a screen with one
 * costs — which is the second half of why the old version was expensive.
 *
 * The stop is not a failure and must not be rendered as one: nothing is lost, the work
 * carries on in the worker, and the screen offers "check again". `isStalled` exists so
 * the page can say that in words instead of silently freezing a spinner.
 */

import { computed, onScopeDispose, ref } from 'vue';

/** Poll index → gap before the NEXT request. Read in order; first match wins. */
export const JOB_POLL_LADDER = [
  { untilPoll: 5, everyMs: 2_000 },
  { untilPoll: 30, everyMs: 5_000 },
];

export const JOB_POLL_SLOW_MS = 15_000;

/** ~15 minutes of watching, then we stop asking. */
export const JOB_POLL_MAX = 80;

/** @param {number} poll How many polls have already happened. */
export const jobPollIntervalMs = poll =>
  JOB_POLL_LADDER.find(step => poll < step.untilPoll)?.everyMs ??
  JOB_POLL_SLOW_MS;

/**
 * @param {() => (void | Promise<void>)} tick What to do on each poll — normally a reload.
 * @param {{ maxPolls?: number }} [options]
 */
export const useJobPoller = (tick, options = {}) => {
  const { maxPolls = JOB_POLL_MAX } = options;

  const pollCount = ref(0);
  const isStalled = ref(false);

  let timer = null;
  let running = false;

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const stop = () => {
    running = false;
    clearTimer();
  };

  // One self-scheduling function rather than a `run`/`schedule` pair: the two would refer
  // to each other, and a mutual reference between a poller and its own timer is the shape
  // that leaves a stray timeout behind when one of them is changed later.
  const run = async () => {
    timer = null;
    if (!running) return;
    pollCount.value += 1;
    try {
      await tick();
    } catch {
      // A failed poll is not a reason to stop polling — the reload it wraps owns its own
      // error state, and the next tick is the retry. Swallowing it here is what keeps a
      // dropped connection from silently killing the watch.
    }
    if (!running) return;
    if (pollCount.value >= maxPolls) {
      isStalled.value = true;
      stop();
      return;
    }
    clearTimer();
    timer = setTimeout(run, jobPollIntervalMs(pollCount.value));
  };

  /** Idempotent: called from a watcher that fires on every response. */
  const start = () => {
    if (running || isStalled.value) return;
    running = true;
    clearTimer();
    timer = setTimeout(run, jobPollIntervalMs(pollCount.value));
  };

  /**
   * "Check again" — after the hard stop, or whenever the owner asks. Resets the ladder to
   * its fast rungs and polls straight away, because somebody is watching again.
   */
  const resume = async () => {
    stop();
    pollCount.value = 0;
    isStalled.value = false;
    running = true;
    await run();
  };

  // A poll that fires into a page the owner has already left is the same class of bug as
  // a response written into an unmounted component — stop with the scope, always.
  onScopeDispose(stop);

  return {
    start,
    stop,
    resume,
    pollCount: computed(() => pollCount.value),
    isStalled: computed(() => isStalled.value),
  };
};

export default useJobPoller;
